import React, { useState, useEffect } from "react";
import "./UserManagement.css";
import AddUserModal from "./AddUserModal";
import { axiosBaseQuery } from "../../api/axiosBaseQuery";
import Modal from "../../components/Modal/Modal";

// assets
import editIcon from "../../assets/new-images/edit-icon.svg";
import deleteIcon from "../../assets/new-images/delete-icon.svg";

const UserManagement = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 10;
    const [users, setUsers] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [userIdToDelete, setUserIdToDelete] = useState(null);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUsers(currentPage, searchTerm);
        }, 300); // Debounce for 300ms

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm]);

    const fetchUsers = async (page = 1, search = "") => {
        const { data, error } = await axiosBaseQuery({
            url: "/app-users",
            method: "GET",
            params: { page, size: itemsPerPage, search }
        });

        if (data && data.items) {
            const mappedUsers = data.items
                .map(user => ({
                    id: user.id,
                    name: user.full_name,
                    email: user.username,
                    mob: user.mobile_no,
                    ugdn: user.ugdn_id,
                    role: user.role,
                    selections: user.selections,
                    active: user.active
                }));
            setUsers(mappedUsers);
            setTotalCount(data.total_count);
        } else if (error) {
            console.error("Error fetching users:", error);
        }
    };


    const handleAddUser = (user) => {
        // Since we are using server side pagination, it's better to refetch
        // current page to ensure data and totalCount are accurate
        fetchUsers(currentPage, searchTerm);
        setIsModalOpen(false);
        setEditingUser(null);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const openAddModal = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleDeleteUser = (userId) => {
        setUserIdToDelete(userId);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (userIdToDelete) {
            const { data, error } = await axiosBaseQuery({
                url: `/app-users/${userIdToDelete}/status`,
                method: "POST",
                params: { active: false }
            });

            if (data) {
                // Since we are using server side pagination, it's better to refetch
                fetchUsers(currentPage, searchTerm);
                setShowDeleteDialog(false);
                setUserIdToDelete(null);
            } else if (error) {
                console.error("Error deleting user:", error);
                alert("Failed to delete user. Please try again.");
            }
        }
    };

    const cancelDelete = () => {
        setShowDeleteDialog(false);
        setUserIdToDelete(null);
    };

    // Pagination logic
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const indexOfFirstUser = (currentPage - 1) * itemsPerPage;
    const currentUsers = users;

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="user-management-container">
            <div className="user-management-header-wrapper">
                <div className="user-management-title-section">
                    <h1 className="user-management-title">User Management</h1>
                    <p className="user-management-subtitle">Manage user accounts and application access</p>
                </div>
                <div className="header-actions">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search by name, email, or UGDN..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="user-search-input"
                        />
                    </div>
                    <button className="add-user-btn" onClick={openAddModal}>
                        <span className="plus-icon">+</span> Add New User
                    </button>
                </div>
            </div>

            <div className="user-management-table-container">
                <table className="user-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            {/* <th>Mobile</th> */}
                            <th>UGDN</th>
                            <th>Role</th>
                            <th>Applications</th>
                            <th className="actions-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="name-cell">
                                    <div className="name-cell-content">

                                        <div className="user-name-wrapper">
                                            <div className="user-name-part">{user.name}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>{user.email}</td>
                                {/* <td>{user.mob}</td> */}
                                <td>{user.ugdn}</td>
                                <td>
                                    <span className={`role-tag-pill ${user.role?.toLowerCase()}`}>
                                        {user.role?.toUpperCase()}
                                    </span>
                                </td>
                                <td>
                                    <div className="applications-badges">
                                        {Object.entries(user.selections || {}).map(([appName, roleName]) => (
                                            <div key={appName} className="app-badge">
                                                <span className="app-name">{appName}</span>
                                                <span className="role-tag">{roleName.toUpperCase()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td className="actions-cell">
                                    <button className="edit-btn" onClick={() => openEditModal(user)}>
                                        <img src={editIcon} alt="edit" />
                                    </button>
                                    <button className="delete-btn" onClick={() => handleDeleteUser(user.id)}>
                                        <img src={deleteIcon} alt="delete" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalCount > 0 && (
                <div className="pagination-container">
                    <div className="pagination-info">
                        Showing <span>{indexOfFirstUser + 1}</span> to <span>{Math.min(indexOfFirstUser + users.length, totalCount)}</span> of <span>{totalCount}</span> records
                    </div>
                    <div className="pagination-controls">
                        <button
                            className="page-btn prev"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>

                        <div className="page-numbers">
                            {[...Array(totalPages)].map((_, index) => {
                                const pageNumber = index + 1;
                                // Show first, last, and pages around current page
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === totalPages ||
                                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={pageNumber}
                                            className={`page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                } else if (
                                    (pageNumber === currentPage - 2 && pageNumber > 1) ||
                                    (pageNumber === currentPage + 2 && pageNumber < totalPages)
                                ) {
                                    return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                                }
                                return null;
                            })}
                        </div>

                        <button
                            className="page-btn next"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <AddUserModal
                    onClose={() => setIsModalOpen(false)}
                    onAddUser={handleAddUser}
                    editData={editingUser}
                />
            )}

            <Modal open={showDeleteDialog} onClose={cancelDelete}>
                <div className="modal-inner-content">
                    {deleteIcon && (
                        <div className="modal-icon">
                            <img src={deleteIcon} alt="Confirm Icon" />
                        </div>
                    )}
                    <div className="modal-title">Confirm Deletion</div>
                    <div className="modal-message">
                        Are you sure you want to delete this user? This action will deactivate the user account.
                    </div>
                    <div className="modal-buttons modal-buttons-circular">
                        <button onClick={cancelDelete} className="button-style no-btn">
                            Cancel
                        </button>
                        <button onClick={confirmDelete} className="button-style yes-btn">
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UserManagement;